import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { User, Lock, Eye, EyeOff, ArrowRight, Check, Landmark } from 'lucide-react';
interface LoginProps {
  onLogin: (userRole: 'maker' | 'checker', userEmail: string) => void;
}
export function Login({
  onLogin
}: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Demo users
  const demoUsers = [{
    email: 'maker@bunyaadfinance.com',
    password: 'maker123',
    role: 'maker' as const,
    name: 'John Maker'
  }, {
    email: 'checker@bunyaadfinance.com',
    password: 'checker123',
    role: 'checker' as const,
    name: 'Sarah Checker'
  }];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    setTimeout(() => {
      const user = demoUsers.find(u => u.email === email && u.password === password);
      if (user) {
        onLogin(user.role, user.email);
      } else {
        setError('Invalid user ID or password');
      }
      setIsLoading(false);
    }, 800);
  };
  const handleDemoLogin = (userEmail: string, userPassword: string) => {
    setEmail(userEmail);
    setPassword(userPassword);
    setError('');
  };
  const features = ['Seamless application management', 'Real-time portfolio tracking', 'Secure multi-role access control'];
  return <div className="min-h-screen w-full bg-slate-100 flex items-center justify-center p-4 sm:p-6 lg:p-10" data-id="element-2104">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-xl overflow-hidden grid grid-cols-1 lg:grid-cols-2" data-id="element-2105">
        {/* Left brand panel */}
        <div className="relative bg-[#0c2436] text-white p-8 sm:p-10 lg:p-12 flex flex-col" data-id="element-2106">
          {/* Subtle grid texture */}
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }} aria-hidden="true" data-id="element-2107" />
          <div className="relative flex flex-col h-full" data-id="element-2108">
            {/* Logo */}
            <div className="flex flex-col items-center text-center mb-10" data-id="element-2109">
              <div className="h-14 w-14 rounded-xl bg-white/10 flex items-center justify-center mb-3" data-id="element-2110">
                <Landmark className="h-7 w-7 text-emerald-400" data-id="element-2111" />
              </div>
              <span className="text-sm font-medium tracking-wide" data-id="element-2112">
                <span className="text-white" data-id="element-2113">Bunyaad</span>{' '}
                <span className="text-emerald-400" data-id="element-2114">Finance</span>
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold leading-tight tracking-tight" data-id="element-2115">
              Bunyaad <span className="text-emerald-400" data-id="element-2116">Finance</span>
            </h1>
            <p className="mt-5 text-base text-slate-300 leading-relaxed max-w-sm" data-id="element-2117">
              Streamline your lending operations with a{' '}
              <span className="font-semibold text-white" data-id="element-2118">
                secure, integrated platform
              </span>{' '}
              — enabling fast and transparent credit decisions.
            </p>

            <ul className="mt-auto pt-10 space-y-4" data-id="element-2119">
              {features.map(feature => <li key={feature} className="flex items-center space-x-3" data-id="element-2120">
                  <span className="h-6 w-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0" data-id="element-2121">
                    <Check className="h-3.5 w-3.5 text-emerald-400" data-id="element-2122" />
                  </span>
                  <span className="text-sm text-slate-200" data-id="element-2123">{feature}</span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Right form panel */}
        <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center" data-id="element-2124">
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight" data-id="element-2125">
            Welcome back
          </h2>
          <p className="mt-2 text-sm text-slate-500" data-id="element-2126">
            Sign in to access your account.
          </p>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit} data-id="element-2127">
            <div data-id="element-2128">
              <label htmlFor="userId" className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2129">
                User ID
              </label>
              <div className="relative" data-id="element-2130">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-2131">
                  <User className="h-4 w-4 text-slate-400" data-id="element-2132" />
                </span>
                <input id="userId" type="text" value={email} onChange={e => setEmail(e.target.value)} required placeholder="Enter your user ID" className="w-full pl-10 pr-3 py-3 text-sm border border-slate-300 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0c2436]/30 focus:border-[#0c2436] transition" data-id="element-2133" />
              </div>
            </div>

            <div data-id="element-2134">
              <label htmlFor="password" className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2135">
                Password
              </label>
              <div className="relative" data-id="element-2136">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-2137">
                  <Lock className="h-4 w-4 text-slate-400" data-id="element-2138" />
                </span>
                <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required placeholder="Enter your password" className="w-full pl-10 pr-11 py-3 text-sm border border-slate-300 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0c2436]/30 focus:border-[#0c2436] transition" data-id="element-2139" />
                <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition" aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-2140">
                  {showPassword ? <EyeOff className="h-4 w-4" data-id="element-2141" /> : <Eye className="h-4 w-4" data-id="element-2142" />}
                </button>
              </div>
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-id="element-2143">
                <p className="text-sm text-red-600" data-id="element-2144">{error}</p>
              </div>}

            <Button type="submit" variant="primary" isLoading={isLoading} className="w-full justify-center bg-[#0c2436] hover:bg-[#102d44] py-3" data-id="element-2145">
              <span className="flex items-center" data-id="element-2146">
                Sign In
                <ArrowRight className="ml-2 h-4 w-4" data-id="element-2147" />
              </span>
            </Button>
          </form>

          {/* Demo credentials */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-xs text-slate-400" data-id="element-2148">
            <span data-id="element-2149">Demo:</span>
            {demoUsers.map(user => <button key={user.email} type="button" onClick={() => handleDemoLogin(user.email, user.password)} className="px-2.5 py-1 rounded-md border border-slate-200 text-slate-500 hover:border-[#0c2436] hover:text-[#0c2436] transition" data-id="element-2150">
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </button>)}
          </div>

          <p className="mt-6 text-center text-xs text-slate-400" data-id="element-2151">
            © 2026 Bunyaad Finance. All rights reserved.
          </p>
        </div>
      </div>
    </div>;
}